import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isAndroidWebView } from "@/lib/inAppOAuth";

// Navegación nativa con gestos en Android/WebView (y móvil en general):
//   · Deslizar desde el borde izquierdo hacia la derecha → Volver (atrás).
//   · Deslizar desde el borde derecho hacia la izquierda → Adelante.
//   · Toque (tap) rápido en el borde izquierdo/derecho → atrás/adelante.
//
// Diseño pensado para no competir con:
//   · Modales/sheets/drawers abiertos (role=dialog / aria-modal / data-state=open).
//   · Inputs, textareas, selects, contenteditable, carruseles y sliders.
//   · Contenedores con scroll horizontal (overflow-x) — se cede el gesto.
//   · Scroll vertical de la página (sólo actúa si el gesto es horizontal).
//
// El gesto se evalúa en el borde de la pantalla (EDGE px) para imitar el
// comportamiento nativo de Android (predictive back gesture) sin invadir el
// área central donde el usuario scrollea o interactúa con controles.
const EDGE = 30;        // ancho de la zona sensible del borde (px)
const SWIPE_MIN = 55;   // distancia mínima del deslizamiento para confirmar (px)
const TAP_MAX = 10;     // movimiento máximo para considerar "toque" (px)
const VERT_MAX = 38;    // deriva vertical máxima para seguir siendo gesto horizontal
const ACTIVE_MS = 420;  // tiempo de permanencia de la pista visual tras soltar

function isInteractive(target) {
  if (!target || target.nodeType !== 1) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  // Carruseles / sliders / elementos arrastrables.
  if (target.closest('[role="slider"],[data-carousel],[data-swipe-exclude],.embla,.glide,.swiper')) return true;
  // Contenedor con scroll horizontal: el gesto cede al scroll nativo.
  let el = target;
  for (let i = 0; i < 6 && el && el.nodeType === 1; i++) {
    const style = window.getComputedStyle(el);
    if ((style.overflowX === "auto" || style.overflowX === "scroll") && el.scrollWidth > el.clientWidth + 2) return true;
    el = el.parentElement;
  }
  return false;
}

function isOverlayOpen() {
  // Cualquier modal / sheet / drawer / popover abierto desactiva los gestos de
  // borde para que el "atrás" cierre el overlay (nativo) en lugar de navegar.
  return !!(
    document.querySelector('[role="dialog"],[aria-modal="true"],[data-state="open"][role="dialog"]') ||
    document.querySelector('[data-sheet-open="true"],[data-drawer-open="true"]')
  );
}

export default function SwipeNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" &&
    (window.matchMedia("(max-width: 1023px)").matches || isAndroidWebView())
  );
  const [hint, setHint] = useState(null); // null | "back" | "forward"
  const [progress, setProgress] = useState(0); // 0..1 profundidad del gesto
  const stateRef = useRef({
    active: false,
    edge: null,        // "left" | "right" | null
    startX: 0,
    startY: 0,
    moved: 0,
    locked: false,
    startT: 0,
  });
  const hintTimer = useRef(null);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const on = () => setIsMobile(mql.matches || isAndroidWebView());
    mql.addEventListener("change", on);
    return () => mql.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const canGoBack = () => window.history.length > 1;
    // Forward: confiamos en el historial del navegador; si no hay entrada
    // hacia adelante, navigate(1) es no-op (no lanza).
    const go = (dir) => {
      try { navigate(dir); } catch { /* noop */ }
    };

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) { stateRef.current.active = false; return; }
      const t = e.touches[0];
      const fromLeft = t.clientX <= EDGE;
      const fromRight = t.clientX >= window.innerWidth - EDGE;
      if (!fromLeft && !fromRight) { stateRef.current.active = false; return; }
      if (isOverlayOpen()) { stateRef.current.active = false; return; }
      if (isInteractive(e.target)) { stateRef.current.active = false; return; }
      stateRef.current = {
        active: true,
        edge: fromLeft ? "left" : "right",
        startX: t.clientX,
        startY: t.clientY,
        moved: 0,
        locked: false,
        startT: Date.now(),
      };
    };

    const onTouchMove = (e) => {
      const s = stateRef.current;
      if (!s.active || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - s.startX;
      const dy = t.clientY - s.startY;
      // Bloquea la dirección tras un movimiento mínimo: sólo horizontal.
      if (!s.locked) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { s.active = false; return; }
        s.locked = true;
      }
      if (Math.abs(dy) > VERT_MAX) { s.active = false; setHint(null); setProgress(0); return; }
      // Sentido correcto según borde: izquierda→derecha (atrás), derecha→izquierda (adelante).
      const valid =
        (s.edge === "left" && dx > 0) ||
        (s.edge === "right" && dx < 0);
      if (!valid) { s.active = false; setHint(null); setProgress(0); return; }
      s.moved = Math.abs(dx);
      setHint(s.edge === "left" ? "back" : "forward");
      setProgress(Math.min(s.moved / SWIPE_MIN, 1));
      // Evita el scroll/overscroll horizontal del navegador durante el gesto.
      if (e.cancelable) e.preventDefault();
    };

    const finish = (tapped) => {
      const s = stateRef.current;
      if (!s.active) { setHint(null); setProgress(0); return; }
      s.active = false;
      const dist = s.moved;
      const dir = s.edge === "left" ? -1 : 1;
      if (dist >= SWIPE_MIN || tapped) {
        // Tap en el borde también cuenta como gesto (toque en pantalla).
        if (dir === -1 && canGoBack()) go(-1);
        else if (dir === 1) go(1);
      }
      setProgress(0);
      setHint(null);
    };

    const onTouchEnd = (e) => {
      const s = stateRef.current;
      if (!s.active) { setHint(null); setProgress(0); return; }
      // Toque rápido y prácticamente sin movimiento → interpretar como tap de borde.
      const elapsed = Date.now() - s.startT;
      const tapped = elapsed < 260 && s.moved <= TAP_MAX;
      finish(tapped);
    };

    const onTouchCancel = () => {
      stateRef.current.active = false;
      setHint(null);
      setProgress(0);
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchCancel, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [isMobile, navigate, location.pathname]);

  // Limpia la pista visual si queda rezagada.
  useEffect(() => {
    if (hint == null) return;
    if (hintTimer.current) clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => { setHint(null); setProgress(0); }, ACTIVE_MS);
    return () => clearTimeout(hintTimer.current);
  }, [hint, progress]);

  if (!isMobile || hint == null) return null;

  const Icon = hint === "back" ? ChevronLeft : ChevronRight;
  const side = hint === "back" ? "left" : "right";

  return createPortal(
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "50%",
        [side]: Math.max(6, Math.min(EDGE, 10 + progress * 14)),
        transform: `translateY(-50%) scale(${0.8 + progress * 0.35})`,
        opacity: 0.35 + progress * 0.5,
        pointerEvents: "none",
        zIndex: 40,
        transition: "opacity .12s ease, transform .12s ease",
      }}
      className="flex items-center justify-center w-9 h-14 bg-cyan/15 border border-cyan/50 text-cyan backdrop-blur-sm"
    >
      <Icon className="w-5 h-5" strokeWidth={2.4} />
    </div>,
    document.body
  );
}